import React, { useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import {
  obtenerCalendarioPagos,
  marcarEgresoPagado,
  obtenerHistorialPagos,
  obtenerNotificacionesPagos,
  marcarNotificacionPagoLeida,
} from '../api/backend_v2';
import { formatearFechaArgentina } from '../utils/fechas';
import { formatearMoneda } from '../utils/moneda';
import {
  XMarkIcon,
  CalendarDaysIcon,
  BanknotesIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import GestionEgresos from '../components/GestionEgresos';

const NOMBRES_DIAS = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'];

function obtenerFechaEvento(item) {
  return item?.fecha_cobro || item?.fecha;
}

function normalizarFecha(valor) {
  if (!valor) return null;
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function construirCuadriculaMensual(eventos, mesSeleccionado) {
  const [anioStr, mesStr] = (mesSeleccionado || '').split('-');
  const anio = Number(anioStr);
  const mes = Number(mesStr);
  if (!anio || !mes) {
    return { celdas: [], detalle: [], titulo: '' };
  }

  const primerDia = new Date(anio, mes - 1, 1);
  const diasEnMes = new Date(anio, mes, 0).getDate();
  const offsetLunes = (primerDia.getDay() + 6) % 7;

  const eventosPorFecha = {};
  eventos.forEach((ev) => {
    const key = normalizarFecha(obtenerFechaEvento(ev));
    if (!key || !key.startsWith(`${mesSeleccionado}-`)) return;
    if (!eventosPorFecha[key]) eventosPorFecha[key] = [];
    eventosPorFecha[key].push(ev);
  });

  const celdas = [];
  for (let i = 0; i < offsetLunes; i += 1) celdas.push(null);
  for (let d = 1; d <= diasEnMes; d += 1) {
    const key = `${mesSeleccionado}-${String(d).padStart(2, '0')}`;
    celdas.push({ dia: d, key, eventos: eventosPorFecha[key] || [] });
  }
  while (celdas.length % 7 !== 0) celdas.push(null);

  const detalle = Object.values(eventosPorFecha)
    .flat()
    .sort((a, b) => new Date(obtenerFechaEvento(a)) - new Date(obtenerFechaEvento(b)));

  const titulo = `${primerDia.toLocaleString('es-AR', { month: 'long' })} ${anio}`;
  return { celdas, detalle, titulo };
}

const CalendarioPagos = ({ isOpen = true, onClose, modoPagina = false }) => {
  const calendarioRef = useRef(null);
  const [calendario, setCalendario] = useState(null);
  const [historial, setHistorial] = useState({ total: 0, total_importe_pagado: 0, registros: [] });
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [loadingNotificaciones, setLoadingNotificaciones] = useState(false);
  const [error, setError] = useState(null);
  const [diasAdelante, setDiasAdelante] = useState(90);
  const [vistaActiva, setVistaActiva] = useState('calendario');
  const [procesandoId, setProcesandoId] = useState(null);
  const [gestionEgresosOpen, setGestionEgresosOpen] = useState(false);
  const [mesSeleccionado, setMesSeleccionado] = useState(() => {
    const ahora = new Date();
    return `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    if (isOpen) {
      cargarCalendario();
      cargarHistorial();
      cargarNotificaciones();
    }
  }, [isOpen, diasAdelante]);

  const cargarCalendario = async () => {
    try {
      setLoading(true);
      setError(null);
      const datos = await obtenerCalendarioPagos(diasAdelante);
      setCalendario(datos);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cargarHistorial = async () => {
    try {
      setLoadingHistorial(true);
      const data = await obtenerHistorialPagos({ solo_pagados: true, limite: 250 });
      setHistorial(data || { total: 0, total_importe_pagado: 0, registros: [] });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingHistorial(false);
    }
  };

  const cargarNotificaciones = async () => {
    try {
      setLoadingNotificaciones(true);
      const data = await obtenerNotificacionesPagos(false, 100);
      setNotificaciones(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingNotificaciones(false);
    }
  };

  const eventosPendientes = useMemo(() => {
    const cheques = (calendario?.proximos_cheques || []).map((c) => ({ ...c, categoria: 'cheque' }));
    const transferencias = (calendario?.proximas_transferencias || []).map((t) => ({ ...t, categoria: t.tipo === 'a_pagar_efet' ? 'a_pagar_efet' : 'transferencia' }));
    return [...cheques, ...transferencias].sort((a, b) => new Date(obtenerFechaEvento(a)) - new Date(obtenerFechaEvento(b)));
  }, [calendario]);

  const cuadricula = useMemo(
    () => construirCuadriculaMensual(eventosPendientes, mesSeleccionado),
    [eventosPendientes, mesSeleccionado]
  );

  const marcarPago = async (item, pagado) => {
    try {
      setProcesandoId(item.id);

      let metodo = item.tipo;
      let observacion = null;

      if (pagado) {
        const respuesta = await Swal.fire({
          title: 'Marcar como pagado',
          html: `
            <input id="metodo_pago" class="swal2-input" placeholder="Metodo de pago" value="${item.tipo || ''}" />
            <input id="observacion_pago" class="swal2-input" placeholder="Observacion (opcional)" />
          `,
          focusConfirm: false,
          showCancelButton: true,
          confirmButtonText: 'Guardar',
          cancelButtonText: 'Cancelar',
          preConfirm: () => {
            const metodoEl = document.getElementById('metodo_pago');
            const obsEl = document.getElementById('observacion_pago');
            return {
              metodo_pago: metodoEl ? metodoEl.value : item.tipo,
              observacion_pago: obsEl ? obsEl.value : null,
            };
          },
        });

        if (!respuesta.isConfirmed) {
          setProcesandoId(null);
          return;
        }

        metodo = (respuesta.value?.metodo_pago || item.tipo || '').trim();
        observacion = (respuesta.value?.observacion_pago || '').trim() || null;
      }

      await marcarEgresoPagado(item.id, {
        pagado,
        metodo_pago: pagado ? metodo : null,
        observacion_pago: pagado ? observacion : null,
      });

      await Promise.all([cargarCalendario(), cargarHistorial(), cargarNotificaciones()]);
    } catch (err) {
      setError(err.message || 'No se pudo actualizar el estado de pago');
    } finally {
      setProcesandoId(null);
    }
  };

  const marcarNotificacionLeida = async (id) => {
    try {
      await marcarNotificacionPagoLeida(id);
      setNotificaciones((prev) => prev.map((n) => (n.id === id ? { ...n, leida: true } : n)));
    } catch (err) {
      setError(err.message);
    }
  };

  const exportarCuadriculaMes = () => {
    const aoa = [];
    aoa.push([`Calendario de pagos - ${cuadricula.titulo}`]);
    aoa.push(NOMBRES_DIAS);

    for (let i = 0; i < cuadricula.celdas.length; i += 7) {
      const semana = cuadricula.celdas.slice(i, i + 7).map((celda) => {
        if (!celda) return '';
        const resumen = celda.eventos
          .map((ev) => `${
            ev.categoria === 'cheque' ? 'CHQ' :
            ev.categoria === 'a_pagar_efet' ? 'EFT' :
            'TRF'
          } ${formatearMoneda(ev.importe)} ${ev.receptor}`)
          .join('\n');
        return `${celda.dia}${resumen ? `\n${resumen}` : ''}`;
      });
      aoa.push(semana);
    }

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = Array.from({ length: 7 }, () => ({ wch: 25 }));

    const detalle = cuadrosDetalle(eventosPendientes, mesSeleccionado);
    const wsDetalle = XLSX.utils.json_to_sheet(detalle);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cuadricula');
    XLSX.utils.book_append_sheet(wb, wsDetalle, 'Detalle');
    XLSX.writeFile(wb, `calendario_pagos_${mesSeleccionado}.xlsx`);
  };

  const imprimirCalendarioComoImagen = async () => {
    try {
      const nodo = calendarioRef.current;
      if (!nodo) {
        setError('No se encontró el calendario para generar la imagen.');
        return;
      }

      const canvas = await html2canvas(nodo, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const enlace = document.createElement('a');
      enlace.href = canvas.toDataURL('image/png');
      enlace.download = `calendario_pagos_${mesSeleccionado || new Date().toISOString().slice(0, 7)}.png`;
      enlace.click();
    } catch (err) {
      setError(err.message || 'No se pudo generar la imagen del calendario.');
    }
  };

  const imprimirCalendarioDirecto = async () => {
    try {
      const nodo = calendarioRef.current;
      if (!nodo) {
        setError('No se encontró el calendario para imprimir.');
        return;
      }

      const canvas = await html2canvas(nodo, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const imagen = canvas.toDataURL('image/png');
      const ventanaImpresion = window.open('', '_blank');
      if (!ventanaImpresion) {
        setError('El navegador bloqueó la ventana de impresión. Habilitá ventanas emergentes.');
        return;
      }

      ventanaImpresion.document.write(`
        <!doctype html>
        <html>
          <head>
            <title>Calendario de pagos</title>
            <style>
              body { margin: 0; padding: 16px; font-family: Arial, sans-serif; background: #fff; }
              h1 { margin: 0 0 12px; font-size: 18px; }
              img { width: 100%; height: auto; }
              @media print {
                body { padding: 0; }
                h1 { margin: 0 0 8px; }
              }
            </style>
          </head>
          <body>
            <h1>Calendario de pagos - ${mesSeleccionado || ''}</h1>
            <img src="${imagen}" alt="Calendario de pagos" />
            <script>
              window.onload = function() {
                window.print();
                window.onafterprint = function() { window.close(); };
              };
            <\/script>
          </body>
        </html>
      `);
      ventanaImpresion.document.close();
    } catch (err) {
      setError(err.message || 'No se pudo abrir la vista de impresión del calendario.');
    }
  };

  const notificacionesSinLeer = notificaciones.filter((n) => !n.leida).length;

  if (!modoPagina && !isOpen) return null;

  return (
    <div
      className={
        modoPagina
          ? 'p-4 sm:p-6 bg-gray-50 min-h-screen'
          : 'fixed inset-0 z-[1050] flex items-center justify-center p-2.5 sm:p-4 overflow-y-auto bg-slate-950/80 backdrop-blur-sm overscroll-contain'
      }
      onClick={(e) => {
        if (!modoPagina && e.target === e.currentTarget && onClose) onClose();
      }}
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl overflow-hidden w-full flex flex-col ${
          modoPagina ? 'max-w-7xl mx-auto shadow-md' : 'max-w-6xl my-auto max-h-[92vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header con esquinas redondeadas automáticas por overflow-hidden */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6 flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-lg sm:text-2xl font-bold flex items-center gap-2">
              <CalendarDaysIcon className="w-5 h-5 sm:w-6 h-6 text-blue-200 shrink-0" />
              <span>Calendario de Pagos</span>
            </h2>
            <p className="text-blue-100 text-xs sm:text-sm mt-0.5 sm:mt-1">Pagos pendientes, historial y notificaciones</p>
          </div>
          {modoPagina ? (
            <button
              onClick={onClose}
              className="px-3.5 py-1.5 sm:px-4 sm:py-2 bg-blue-500 hover:bg-blue-400 rounded-xl transition text-xs sm:text-sm font-semibold"
            >
              Volver a Finanzas
            </button>
          ) : (
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 hover:bg-blue-500/80 rounded-xl transition text-white"
              title="Cerrar modal"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Scrollable Container para cuerpo y controles */}
        <div className="overflow-y-auto flex-1 overscroll-contain">

        {/* Controls: Responsive layout sin desbordes */}
          <div className="p-4 sm:p-6 border-b border-gray-200 bg-gray-50/60 flex flex-col gap-3.5">
            {/* Fila 1: Selectores y Botones de exportar/imprimir */}
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
              {/* Selectores */}
              <div className="grid grid-cols-2 gap-2.5 sm:flex sm:items-end sm:gap-3 w-full lg:w-auto">
                <div className="flex-1 sm:w-44">
                  <label htmlFor="dias-adelante" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                    Próximos días
                  </label>
                  <select
                    id="dias-adelante"
                    value={diasAdelante}
                    onChange={(e) => setDiasAdelante(Number.parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-xs"
                  >
                    <option value={30}>30 días</option>
                    <option value={60}>60 días</option>
                    <option value={90}>90 días</option>
                    <option value={180}>6 meses</option>
                    <option value={365}>1 año</option>
                  </select>
                </div>

                <div className="flex-1 sm:w-48">
                  <label htmlFor="mes-cuadricula" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                    Mes cuadrícula
                  </label>
                  <input
                    id="mes-cuadricula"
                    type="month"
                    value={mesSeleccionado}
                    onChange={(e) => setMesSeleccionado(e.target.value)}
                    className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-xs"
                  />
                </div>
              </div>

              {/* Botones de acción rápida: Guardar imagen / Imprimir */}
              <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-2 w-full lg:w-auto">
                <button
                  type="button"
                  onClick={imprimirCalendarioComoImagen}
                  className="px-3.5 py-2 text-xs sm:text-sm font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-xl shadow-xs transition flex items-center justify-center gap-1.5"
                >
                  <ArrowDownTrayIcon className="w-4 h-4 shrink-0" />
                  <span className="truncate">Guardar imagen</span>
                </button>

                <button
                  type="button"
                  onClick={imprimirCalendarioDirecto}
                  className="px-3.5 py-2 text-xs sm:text-sm font-semibold bg-slate-700 hover:bg-slate-800 text-white rounded-xl shadow-xs transition flex items-center justify-center gap-1.5"
                >
                  <PrinterIcon className="w-4 h-4 shrink-0" />
                  <span>Imprimir</span>
                </button>
              </div>
            </div>

            {/* Fila 2: Pestañas de Navegación y Botón Gestionar Egresos */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 pt-2 border-t border-gray-200">
              {/* Pestañas tipo Segmented Control */}
              <div className="grid grid-cols-3 gap-1 bg-gray-200/80 p-1 rounded-xl w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setVistaActiva('calendario')}
                  className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-bold transition text-center ${
                    vistaActiva === 'calendario'
                      ? 'bg-white text-blue-700 shadow-xs'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Pendientes
                </button>
                <button
                  type="button"
                  onClick={() => setVistaActiva('historial')}
                  className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-bold transition text-center ${
                    vistaActiva === 'historial'
                      ? 'bg-white text-blue-700 shadow-xs'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Historial
                </button>
                <button
                  type="button"
                  onClick={() => setVistaActiva('notificaciones')}
                  className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-bold transition text-center flex items-center justify-center gap-1 ${
                    vistaActiva === 'notificaciones'
                      ? 'bg-white text-blue-700 shadow-xs'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <span className="truncate">Notificaciones</span>
                  {notificacionesSinLeer > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0">
                      {notificacionesSinLeer}
                    </span>
                  )}
                </button>
              </div>

              {/* Botón Gestionar Egresos: sin desbordar nunca en mobile */}
              <button
                type="button"
                onClick={() => setGestionEgresosOpen(true)}
                className="w-full sm:w-auto px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold bg-red-600 hover:bg-red-700 active:bg-red-800 text-white transition-colors flex items-center justify-center gap-1.5 shadow-xs"
              >
                <BanknotesIcon className="w-4 h-4 text-red-200 shrink-0" />
                <span>Gestionar Egresos</span>
              </button>
            </div>
          </div>

          {gestionEgresosOpen && (
            <GestionEgresos onClose={() => setGestionEgresosOpen(false)} />
          )}

          {/* Content */}
          <div ref={calendarioRef} className="p-4 sm:p-6">
          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-600 text-lg">Cargando calendario...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              <p className="font-semibold">Error al cargar calendario:</p>
              <p>{error}</p>
            </div>
          ) : (
            <>
              {vistaActiva === 'calendario' && calendario && (
                <div className="space-y-8">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4 shadow-xs">
                      <p className="text-xs sm:text-sm font-semibold text-blue-700">Cheques pendientes</p>
                      <p className="text-lg sm:text-xl font-bold text-blue-900 mt-0.5">{formatearMoneda(calendario.total_pendiente_cheques)}</p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3 sm:p-4 shadow-xs">
                      <p className="text-xs sm:text-sm font-semibold text-green-700">Transferencias pendientes</p>
                      <p className="text-lg sm:text-xl font-bold text-green-900 mt-0.5">{formatearMoneda(calendario.total_pendiente_transferencias)}</p>
                    </div>
                    <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 sm:p-4 shadow-xs">
                      <p className="text-xs sm:text-sm font-semibold text-rose-700">Total pendiente</p>
                      <p className="text-lg sm:text-xl font-bold text-rose-900 mt-0.5">
                        {formatearMoneda(calendario.total_pendiente_cheques + calendario.total_pendiente_transferencias)}
                      </p>
                    </div>
                  </div>

                  <div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 mb-2.5">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 capitalize">
                        Cuadrícula mensual: {cuadricula.titulo}
                      </h3>
                      {/* Indicador de scroll horizontal en mobile */}
                      <div className="sm:hidden flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1 w-fit">
                        <ArrowsRightLeftIcon className="w-3.5 h-3.5 shrink-0" />
                        <span>Deslizá horizontalmente para ver el mes completo</span>
                      </div>
                    </div>

                    {/* Contenedor con scroll horizontal para mobile */}
                    <div className="overflow-x-auto pb-3 -mx-4 px-4 sm:mx-0 sm:px-0">
                      <div className="min-w-[700px]">
                        {/* Cabecera de días */}
                        <div className="grid grid-cols-7 gap-2 text-xs sm:text-sm font-bold text-gray-600 mb-2 bg-gray-50 rounded-xl p-1 border border-gray-100">
                          {NOMBRES_DIAS.map((d) => (
                            <div key={d} className="text-center py-1.5">{d}</div>
                          ))}
                        </div>

                        {/* Celdas */}
                        <div className="grid grid-cols-7 gap-2">
                          {cuadricula.celdas.map((celda, idx) => (
                            <div
                              key={`${celda?.key || 'empty'}-${idx}`}
                              className={`min-h-[115px] sm:min-h-[130px] rounded-xl border p-2 sm:p-2.5 transition-all ${
                                celda
                                  ? celda.eventos.length > 0
                                    ? 'bg-white border-blue-200 shadow-xs ring-1 ring-blue-50'
                                    : 'bg-white border-gray-200 hover:border-gray-300'
                                  : 'bg-gray-50/60 border-transparent'
                              }`}
                            >
                              {celda && (
                                <>
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-sm sm:text-base font-extrabold text-gray-800">{celda.dia}</span>
                                    {celda.eventos.length > 0 && (
                                      <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0"></span>
                                    )}
                                  </div>
                                  <div className="space-y-1.5">
                                    {celda.eventos.slice(0, 2).map((ev) => (
                                      <div
                                        key={ev.id}
                                        className={`px-2 py-1 rounded-lg text-xs leading-tight ${
                                          ev.categoria === 'cheque'
                                            ? 'bg-blue-100 text-blue-900 border border-blue-200/60'
                                            : ev.categoria === 'a_pagar_efet'
                                            ? 'bg-orange-100 text-orange-900 border border-orange-200/60'
                                            : 'bg-emerald-100 text-emerald-900 border border-emerald-200/60'
                                        }`}
                                      >
                                        <div className="font-bold">
                                          {ev.categoria === 'cheque'
                                            ? 'CHQ'
                                            : ev.categoria === 'a_pagar_efet'
                                            ? 'EFT'
                                            : 'TRF'}{' '}
                                          {formatearMoneda(ev.importe)}
                                        </div>
                                        <div className="truncate text-[11px] font-medium opacity-90">
                                          {ev.receptor || 'Sin receptor'}
                                        </div>
                                      </div>
                                    ))}
                                    {celda.eventos.length > 2 && (
                                      <div className="text-[11px] font-semibold text-blue-600 bg-blue-50/80 rounded px-1 text-center">
                                        +{celda.eventos.length - 2} más
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Detalle de pendientes</h3>
                    {eventosPendientes.length === 0 ? (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center text-gray-600">
                        No hay pagos pendientes para el período seleccionado.
                      </div>
                    ) : (
                      <div className="grid gap-3">
                        {eventosPendientes.map((item) => (
                          <div
                            key={item.id}
                            className={`p-4 rounded border-l-4 ${
                              item.categoria === 'cheque' ? 'bg-blue-50 border-blue-500' :
                              item.categoria === 'a_pagar_efet' ? 'bg-orange-50 border-orange-500' :
                              'bg-green-50 border-green-500'
                            }`}
                          >
                            <div className="flex flex-wrap justify-between items-start gap-3">
                              <div>
                                <p className="font-semibold text-gray-800">{item.receptor}</p>
                                <p className="text-sm text-gray-600">
                                  {item.categoria === 'cheque' ? `Cheque ${item.nro_cheque || ''}` :
                                   item.categoria === 'a_pagar_efet' ? 'A pagar (efect.)' :
                                   'Transferencia'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Fecha evento: {formatearFechaArgentina(obtenerFechaEvento(item))}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-gray-900">{formatearMoneda(item.importe)}</p>
                                <button
                                  type="button"
                                  onClick={() => marcarPago(item, true)}
                                  disabled={procesandoId === item.id}
                                  className="mt-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm disabled:opacity-50"
                                >
                                  {procesandoId === item.id ? 'Procesando...' : 'Marcar pagado'}
            </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {vistaActiva === 'historial' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Historial de pagos realizados</h3>
                  <div className="mb-3 text-sm text-gray-700">
                    Total pagado: <span className="font-bold">{formatearMoneda(historial.total_importe_pagado || 0)}</span>
                  </div>
                  {loadingHistorial ? (
                    <div className="text-gray-600">Cargando historial...</div>
                  ) : (historial.registros || []).length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-600">No hay registros en historial.</div>
                  ) : (
                    <div className="space-y-2">
                      {(historial.registros || []).map((item) => (
                        <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap justify-between items-center gap-3">
                          <div>
                            <p className="font-semibold text-gray-800">{item.receptor}</p>
                            <p className="text-sm text-gray-600">{item.tipo} | Pago: {formatearFechaArgentina(item.fecha_pago || item.updated_at)}</p>
                            <p className="text-xs text-gray-500">Método: {item.metodo_pago || 'Sin especificar'}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-900">{formatearMoneda(item.importe)}</p>
                            <button
                              type="button"
                              onClick={() => marcarPago(item, false)}
                              disabled={procesandoId === item.id}
                              className="mt-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm disabled:opacity-50"
                            >
                              Reabrir pendiente
            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {vistaActiva === 'notificaciones' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Notificaciones de pagos (app)</h3>
                  {loadingNotificaciones ? (
                    <div className="text-gray-600">Cargando notificaciones...</div>
                  ) : notificaciones.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-600">No hay notificaciones de pagos.</div>
                  ) : (
                    <div className="space-y-2">
                      {notificaciones.map((n) => (
                        <div
                          key={n.id}
                          className={`rounded-lg border p-3 ${n.leida ? 'bg-gray-50 border-gray-200' : 'bg-indigo-50 border-indigo-200'}`}
                        >
                          <div className="flex flex-wrap justify-between items-start gap-3">
                            <div>
                              <p className="font-semibold text-gray-800">{n.titulo}</p>
                              <p className="text-sm text-gray-700">{n.mensaje}</p>
                              <p className="text-xs text-gray-500 mt-1">{formatearFechaArgentina(n.fecha_creacion)}</p>
                            </div>
                            {!n.leida && (
                              <button
                                type="button"
                                onClick={() => marcarNotificacionLeida(n.id)}
                                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm"
                              >
                                Marcar leida
            </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        </div>

        {!modoPagina && (
          <div className="bg-gray-50 border-t border-gray-200 p-3 sm:p-4 flex justify-end shrink-0">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-xl text-sm transition"
            >
              Cerrar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

function cuadrosDetalle(eventos, mesSeleccionado) {
  return eventos
    .filter((ev) => {
      const key = normalizarFecha(obtenerFechaEvento(ev));
      return key && key.startsWith(`${mesSeleccionado}-`);
    })
    .map((ev) => ({
      Fecha: formatearFechaArgentina(obtenerFechaEvento(ev)),
      Tipo: ev.categoria === 'cheque' ? 'Cheque' : 'Transferencia',
      Receptor: ev.receptor,
      Importe: Number(ev.importe || 0),
      Referencia: ev.nro_cheque || '',
    }));
}

CalendarioPagos.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  modoPagina: PropTypes.bool,
};

export default CalendarioPagos;

